<template>
    <div>
        <MainModal 
            :show-modal="showModal" 
            size="sm" 
            :title="titleCopy" 
            @close="onCancel"
        >
            <template #body>
                <form :class="{ 'form__max-height': currentFieldsLength > 6 }" @submit.prevent="">
                    <div class="pl--5 pr--5 scroll-height">
                        <FormInput 
                            v-if="hasField('title')"
                            id="title" 
                            v-model="form.title"
                            type="text" 
                            size="sm" 
                            info-type="required"
                            html-for="title" 
                            label="Title"
                        />

                        <FormTextArea 
                            v-if="hasField('description')"
                            id="description" 
                            v-model="form.description"
                            type="text" 
                            size="sm" 
                            no-margin-bottom
                            html-for="description" 
                            label="Provide a description for this project"
                        />

                        <div v-if="hasField('priority') && hasField('status')" class="form__row mb--15 pl--3">
                            <div class="flex-1 mr--5">
                                <FormLabel title="Priority" html-for="priority" />
                                <select v-model="form.priority" class="form-select form-select-sm text--sm text--capitalize">
                                    <option v-for="priority in priorities" :key="priority" :value="priority">
                                        {{ priority }}
                                    </option>
                                </select>
                            </div>
                            <div class="flex-1 mr--5">
                                <FormLabel title="Status" html-for="status" />
                                <select v-model="form.status" class="form-select form-select-sm text--sm text--capitalize" aria-label="Default select example">
                                    <option v-for="status in statuses" :key="status" :value="status">
                                        {{ status }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div v-if="hasField('startDate') || hasField('deadline')" class="form__row mb--15">
                            <div class="flex-1 mr--5">
                                <FormLabel title="Start date" />
                                <v-date-picker
                                    v-model="form.startDate" class="text--sm" mode="dateTime" borderless
                                >
                                    <template #default="{ inputValue, inputEvents }">
                                        <input
                                            class="px-2 py-1 border rounded due_date form-control form-control-sm text--sm"
                                            :value="inputValue"
                                            nav-visibility="click"
                                            v-on="inputEvents"
                                        >
                                    </template>
                                </v-date-picker>
                            </div>

                            <div class="flex-1">
                                <FormLabel title="Deadline" />
                                <v-date-picker
                                    v-model="form.deadline" class="text--sm" :min-date="form.startDate" mode="dateTime"
                                    borderless is24hr title-position="left"
                                >
                                    <template #default="{ inputValue, togglePopover }">
                                        <input
                                            class="px-2 py-1 border rounded due_date form-control form-control-sm text--sm"
                                            :value="inputValue"
                                            nav-visibility="click"
                                            @click="togglePopover"
                                        >
                                    </template>
                                </v-date-picker>
                            </div>
                        </div>

                        <div v-if="hasField('projectType')" class="mb--5">
                            <FormLabel title="Is this a paid project?" html-for="important" />
                            <select v-model="form.projectType" class="form-select form-select-sm text--capitalize pl--10">
                                <option v-for="item in ['paid', 'not paid']" :key="item" :value="item" class="text--capitalize">
                                    {{ item }}
                                </option>
                            </select>
                        </div>

                        <template v-if="form.projectType === 'paid'">
                            <div v-if="hasField('currency')" class="mb--15 mt--15">
                                <FormLabel title="Currency" html-for="currency" />
                                <CustomDropdown 
                                    id="currencyDropdown" 
                                    v-model="form.currency" 
                                    :options="initialCurrencyList"
                                    list-type="currencies" 
                                    index-label="currency" 
                                    :add-on="selectedCurrency.symbol"
                                    @search="searchCurrencies" 
                                    @select="selectCurrency" 
                                />
                            </div>

                            <template v-if="!useRateCard">
                                <div v-if="hasField('billableHours')" class="mb--15">
                                    <FormLabel title="Billable Hours" html-for="billableHours" />
                                    <money
                                        v-model="form.billableHours" v-bind="billableHoursConfig"
                                        class="form-control form-control-sm" spellcheck="false"
                                        @keydown.native="preventKeys"
                                    />
                                </div>

                                <div v-if="hasField('nonBillableHours')" class="mb--15">
                                    <FormLabel title="Non-billable Hours" html-for="nonBillableHours" />
                                    <money
                                        v-model="form.nonBillableHours" v-bind="billableHoursConfig"
                                        class="form-control form-control-sm" spellcheck="false"
                                        @keydown.native="preventKeys"
                                    />
                                </div>

                                <div v-if="hasField('estimatedRevenue')" class="mb--15">
                                    <FormLabel title="Estimated Revenue" html-for="estimatedRevenue" />
                                    <money
                                        v-model="form.estimatedRevenue" v-bind="estimatedRevenueConfig"
                                        class="form-control form-control-sm" spellcheck="false"
                                        @keydown.native="preventKeys"
                                    />
                                </div>
                            </template>

                            <template v-else>
                                <FormLabel title="Select one or more rate cards" html-for="rateCards" />
                                <CustomDropdown 
                                    id="rateCardDropdown" 
                                    v-model="form.rateCards" 
                                    :options="rateCards"
                                    list-type="rate cards" 
                                    index-label="title" 
                                    multiple 
                                    class="pl--3"
                                    :loading="dropdownLoading"
                                    @search="searchRateCards" 
                                    @select="addRateCard" 
                                />
                            </template>
                        </template>
                        
                        <div v-if="hasField('trackProject')" class="mb--15">
                            <FormLabel title="Track Project?" html-for="important" />
                            <select v-model="form.trackProject" class="form-select form-select-sm text--capitalize">
                                <option v-for="item in ['Yes', 'No']" :key="item" :value="item" class="text--capitalize">
                                    {{ item }}
                                </option>
                            </select>
                        </div>

                        <template v-if="form.trackProject === 'Yes'">
                            <div v-if="hasField('trackingType')" class="mb--15">
                                <FormLabel title="Tracking type" html-for="important" />
                                <select v-model="form.trackingType" class="form-select form-select-sm text--capitalize">
                                    <option v-for="item in ['automatic', 'scheduled']" :key="item" :value="item" class="text--capitalize">
                                        {{ item }}
                                    </option>
                                </select>
                            </div>

                            <div v-if="form.trackingType === 'scheduled'" class="mb--15">
                                <div class="mt--15">
                                    <FormLabel title="Start time" html-for="important" />
                                    <v-date-picker
                                        v-model="form.startDate" class="text--sm" mode="dateTime" color="blue"
                                        name="dueDate" nav-visibility="click"
                                        borderless is24hr title-position="left"
                                    >
                                        <template #default="{ inputValue, togglePopover }">
                                            <input
                                                class="px-2 py-1 border rounded due_date form-control form-control-sm text--sm"
                                                :value="inputValue"
                                                @click="togglePopover"
                                            >
                                        </template>
                                    </v-date-picker>
                                </div>
                                <div class="mt--15">
                                    <FormLabel title="End time" html-for="important" />
                                    <v-date-picker
                                        v-model="form.deadline" class="text--sm" mode="dateTime" color="blue"
                                        name="dueDate" nav-visibility="click" borderless is24hr 
                                        title-position="left"
                                    >
                                        <template #default="{ inputValue, togglePopover }">
                                            <input
                                                class="px-2 py-1 border rounded due_date form-control form-control-sm text--sm"
                                                :value="inputValue"
                                                @click="togglePopover"
                                            >
                                        </template>
                                    </v-date-picker>
                                    <div class="flex align-items-center mt--10">
                                        <IconSvg name="info" />
                                        <p class="form-text text--xs ml--5 mt--0">
                                            Note that tracking ends when the project deadline is reached.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <div v-if="hasField('reminders')" class="mt--15">
                            <FormLabel title="Set reminders" html-for="important" />
                            <div>
                                <div v-for="day in reminderEnum" :key="day" class="form-check form-check-inline">
                                    <input
                                        :id="`${day.id}Id`" v-model="form.reminders" 
                                        :value="day.id" class="form-check-input" type="checkbox"
                                    >
                                    <label class="form-check-label text--xs text--capitalize" :for="`${day.id}Id`">
                                        {{ day.name }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        class="mr--5" label="Cancel" variant="outline"
                        @submit="onCancel"
                    />
                    <BaseButton 
                        type="submit" 
                        :label="btnCopy"
                        :loading="loading" 
                        :disabled="!form.title" 
                        @submit="handleSubmit"
                    />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import MainModal from "./MainModal.vue";
import { projectFilterStatuses, priorities } from "@/utils/enums";
import { reminderEnum, days } from "@/utils/dummy";
import FormLabel from "../forms/FormLabel.vue";
import BaseButton from "../buttons/BaseButton.vue";
import FormInput from "../input/FormInput.vue";
import FormTextArea from "../input/FormTextArea";
import CustomDropdown from "../dropdowns/CustomDropdown.vue";
import IconSvg from "../icons/Icon-Svg.vue";
import { assembleQueryList } from "@/utils/helpers";
import { updateProjectDetailsFields } from "@/components/dataComponents/auth";
import { createProjectPayload } from "@/components/dataComponents/projects";

export default {
    name: "CreateProjectModal",

    components: {
        MainModal,
        FormLabel,
        BaseButton, 
        IconSvg,
        FormInput, 
        CustomDropdown,
        FormTextArea,
    },

    props: {
        showModal: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        actionType: { type: String, default: "add" },
        currentProject: { type: Object, default: undefined },
        currentSelection: { type: String, default: "" },
    },

    data() {
        return {
            statuses: projectFilterStatuses,
            form: {
                _id: "",
                title: "",
                description: "",
                status: "to do",
                deadline: null,
                startDate: new Date(),
                priority: "low",
                projectType: "",
                isFavourite: "no",
                tags: [],
                clients: [],
                rateCards: [],
                currency: "",
                billableHours: 0,
                nonBillableHours: 0,
                estimatedRevenue: 0,
                reminders: [],
                trackProject: "",
                trackingDays: [],
                trackingType: "",
            },
            priorities,
            reminderEnum,
            favorites: ["no", "yes"],
            initialCurrencyList: [],
            billableHoursConfig: {
                decimal: ".",
                thousands: ",",          // The currency name or symbol followed by a space.
                precision: 0,
                masked: false,
            },
            dropdownLoading: false,
            useRateCard: false,
            fields: updateProjectDetailsFields,
            selectedCurrency: {
                currency: "", 
                symbol: "",
            },
            currencyMap: null,
            trackingDays: days,
            startTime: null,
            endTime: null,
            rateCards: [],
            internalCurrentSelection: "",
        };
    },

    computed: {
        titleCopy() {
            return this.fields[this.currentSelection]?.title || "";
        },

        clients() {
            return this.$store.getters["clients/clients"] || [];
        },

        appCurrency() {
            return this.$store.getters["settings/general"]?.currency;
        },

        btnCopy() {
            return this.fields[this.currentSelection]?.cta || "Create";
        },

        estimatedRevenue() {
            if (!this.form.rateCards.length) return 0;

            const total = this.form.rateCards.reduce((previous, current) => previous + current.amount, 0);

            return total;
        },

        currentFieldsLength() {
            if (!this.fields || !this.internalCurrentSelection) return 0;

            return Object.keys(this.fields[this.internalCurrentSelection].fields).length;
        },

        estimatedRevenueConfig() {
            const selectItem = this.selectedCurrency?.currency && this.selectedCurrency || this.currentProject;
            const selectCurrency = selectItem?.currency?.toLowerCase();

            if (!selectCurrency || !this.currencyMap) {
                return {
                    precision: 0,
                    decimal: ".",
                    thousands: ",",
                    masked: false,
                    prefix: "",
                };
            }

            return {
                precision: 0,
                decimal: ".",
                thousands: ",",
                masked: false,
                prefix: this.currencyMap[selectCurrency].symbol,
            };
        },

        // computeDeadlineList() {
        //     const currentDate = Date.now()
        //     const deadlineDate = new Date(this.form.deadline).getTime();
        //     const diff = deadlineDate - currentDate;
        //     const daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
        //     const deadlineList = [];

        //     // if the current deadline is greater than 7 or 14, return only specific part of the reminder list
        // }
    },

    watch: {
        currentProject: {
            handler(newVal) {
                if (newVal?._id) {
                    this.prefillForm(newVal);
                }
            },
            immediate: true,
            deep: true,
        },

        "form.trackProject": {
            handler(newVal, oldVal) {
                if (oldVal === "Yes" && newVal === "No") {
                    this.form.trackingDays = [];
                }

                if (oldVal === "No" && newVal === "Yes") {
                    this.form.trackingDays = this.currentProject.trackingDays;
                }
            },
        },

        "form.currency": {
            async handler(newVal) {
                if (newVal && this.useRateCard) {
                    await this.searchRateCards({ currency: newVal });
                }
            },
        },

        "form.projectType"(newVal){            
            if (newVal === "paid") {
                this.internalCurrentSelection = "create_paid";
            } else {
                this.internalCurrentSelection = this.currentSelection;
            }
        },

        "currentSelection": {
            handler(newVal) {
                if (newVal) {
                    this.internalCurrentSelection = newVal;

                    if (newVal === "create_paid") {
                        this.prefillForm({
                            ...this.form,
                            projectType: "paid",
                            currency: this.appCurrency,
                            billableHours: 0,
                            nonBillableHours: 0,
                            estimatedRevenue: 0,
                        });
                    } else {
                        this.prefillFormDataBySelection(newVal);
                    }
                }
            },
        },
    },

    async mounted() {
        this.currencyMap = this.$supported_currencies_map;
        this.initialCurrencyList = Object.values(this.$currencies_obj);
        const rateCards = this.$store.getters["ratecards/rateCards"];

        if (!rateCards.length) {
            await this.searchRateCards();
        } else {
            this.rateCards = rateCards;
        }
    },

    methods: {
        hasField(field) {
            return !this.fields[this.internalCurrentSelection] ? false : this.fields[this.internalCurrentSelection].fields[field]; 
        },

        prefillFormDataBySelection(val) {
            const selectedItem = this.fields[val]?.fields;

            for (const key in selectedItem) {
                if (this.currentProject && this.currentProject.hasOwnProperty(key)) {
                    this.form[key] = this.currentProject[key];
                }
            }
        },

        selectCurrency(item) {
            const index = item && item.toLowerCase() || "";

            this.selectedCurrency = this.currencyMap[index];
            this.form.currency = item;
        },

        searchCurrencies(val) {
            const initialList = this.initialCurrencyList;

            if (val) {
                const filteredList = initialList.filter(item => item.toLowerCase().includes(val));
                this.initialCurrencyList = filteredList;
            } else {
                this.initialCurrencyList = Object.values(this.$currencies_obj);
            }
        },

        preventKeys(event) {
            if ((event.keyCode >= 48 && event.keyCode <= 57) || (event.keyCode >= 96 && event.keyCode <= 105) || event.keyCode == 8 || event.keyCode == 9 || event.keyCode == 190 || event.keyCode == 37 || event.keyCode == 39) {
            } else {
                return event.preventDefault();
            }
        },

        prefillForm(val) {
            this.form = {
                ...this.form,
                ...val,
                isFavourite: val?.isFavourite ? "yes" : "no",
                currency:  val?.currency || this.appCurrency,
                billingType:  val.billingType || "flat",
                trackProject: val.trackProject ? "Yes" : "No",
                nonBillableHours: val.nonBillableHours || "table",
            };
        },

        addTag(newTag) {
            this.form.tags.push(newTag);
        },

        addRateCard(item) {
            this.form.rateCards = [ ...this.form.rateCards, item ];
        },

        removeRateCard(item) {
            this.form.rateCards = this.form.rateCards.filter(x => x._id !== item._id);
        },

        resetForm() {
            this.form = {
                _id: "",
                title: "",
                description: "",
                status: "to do",
                deadline: null,
                startDate: new Date(),
                priority: "low",
                projectType: "",
                isFavourite: "no",
                tags: [],
                clients: [],
                rateCards: [],
                currency: "",
                billableHours: 0,
                nonBillableHours: 0,
                estimatedRevenue: 0,
                reminders: [],
                trackProject: "",
                trackingDays: [],
                trackingType: "",
                trackingType: "",
            };

            this.selectedCurrency = {
                currency: "", 
                symbol: "",
            };
        },

        onCancel() {
            if (this.actionType === "add") {
                this.resetForm();
            }
            this.$emit("close");
        },

        setSelected(value) {
            this.form.clients = [ value, ...this.form.clients ];
        },

        buildPayload() {
            const payload = createProjectPayload(this);

            if (this.actionType === "edit" && this.form._id) {
                payload._id = this.form._id;
            }

            payload.deadline = new Date(this.form.deadline).getTime();
            payload.startDate = new Date(this.form.startDate).getTime();

            return payload;
        },

        handleSubmit() {
            const payload = this.buildPayload();
            const data = { payload, resetFn: this.resetForm };

            this.$emit("complete", data);    
        },

        async searchRateCards(params) {
            try {
                this.dropdownLoading = true;

                const pageQueryObj = {
                    page: 1,
                    title: params?.title,
                    currency: params?.currency,
                };

                const buildResult = assembleQueryList(pageQueryObj);
                const res = await this.$store.dispatch("ratecards/fetchRateCards",  { 
                    payload: buildResult, 
                    refresh: true, 
                    external: true, 
                });

                // console.log({ searchRateCards: res });
                
                this.rateCards = res;
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.dropdownLoading = false;
            }
        },

        setReminders() {
            this.form.reminders = [];

            for (let i = 0; i < this.remindersNames.length; i++) {
                this.form.reminders.push({
                    remindersName: this.remindersNames[i],
                    reminderDate: new Date(),
                });
            } 
        },

        togglePaymentSelection() {
            if (this.useRateCard) {
                this.useRateCard = false;
                this.form.rateCards = [];
            } else {
                this.useRateCard = true;
                this.form.billableHours = 0;
                this.form.estimatedRevenue = 0;
            }
        },

        upgradeAccount() {
            this.onCancel();
            this.$router.push("/dashboard/settings/payments-and-billings");
        },
    },
};
</script>